import { useState } from "react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      setEmail("");
      setTimeout(() => setStatus("idle"), 4000);
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append("email", email);

      const response = await fetch(
        "https://readdy.ai/api/form/d9187iobhec1he3opq8g",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData.toString(),
        },
      );

      if (response.ok) {
        setStatus("success");
        setEmail("");
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }

    setTimeout(() => setStatus("idle"), 4000);
  };

  return (
    <footer className="bg-background-950 border-t border-primary-500/15">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          <div>
            <div className="bg-background-50 rounded-xl px-5 py-3 inline-flex items-center justify-center mb-6">
              <span className="relative block h-12 w-[150px] overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/8fc9fa8a-5b51-42e8-a68d-8e20b58b33f0.png"
                  alt="Jennifer Siqueira Studio de Beleza"
                  title="Jennifer Siqueira Studio de Beleza Blumenau"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-auto"
                />
              </span>
            </div>
            <p className="text-background-100/60 text-sm leading-relaxed font-light">
              Transformando a beleza com excelência e cuidado personalizado.
              Cada detalhe pensado para realçar sua essência.
            </p>
          </div>

          <div>
            <h4 className="font-label text-xs font-semibold text-primary-300 uppercase tracking-[0.18em] mb-6">
              Navegação
            </h4>
            <ul className="space-y-3">
              {[
                { label: "Início", href: "#hero" },
                { label: "Sobre", href: "#sobre" },
                { label: "Serviços Faciais", href: "#servicos-faciais" },
                { label: "Sobrancelhas", href: "#servicos-sobrancelhas" },
                { label: "Depoimentos", href: "#depoimentos" },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-background-100/60 hover:text-primary-300 text-sm font-light transition-colors duration-300"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-label text-xs font-semibold text-primary-300 uppercase tracking-[0.18em] mb-6">
              Contato
            </h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://wa.me/5547991654435"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-background-100/60 hover:text-primary-300 text-sm font-light transition-colors duration-300 flex items-center gap-2"
                >
                  <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center"></i>
                  (47) 99165-4435
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/jennifersiqueirastudio/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-background-100/60 hover:text-primary-300 text-sm font-light transition-colors duration-300 flex items-center gap-2"
                >
                  <i className="ri-instagram-line w-4 h-4 flex items-center justify-center"></i>
                  @jennifersiqueirastudio
                </a>
              </li>
              <li className="text-background-100/60 text-sm font-light flex items-start gap-2">
                <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center mt-0.5"></i>
                <span>
                  Rua João José de Souza, 223
                  <br />
                  Bairro Fortaleza — Blumenau/SC
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-label text-xs font-semibold text-primary-300 uppercase tracking-[0.18em] mb-6">
              Newsletter
            </h4>
            <p className="text-background-100/60 text-sm font-light mb-4">
              Receba novidades e dicas exclusivas de cuidados com a pele.
            </p>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col gap-3"
            >
              <input
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="honeypot-field"
              />
              <div className="flex">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="flex-1 bg-background-900 border border-background-800 rounded-l-full px-4 py-2.5 text-sm text-background-50 placeholder:text-background-100/35 focus:outline-none focus:border-primary-500/50 transition-colors font-light"
                />
                <button
                  type="submit"
                  className="bg-primary-500 hover:bg-primary-600 text-background-950 px-4 py-2.5 rounded-r-full transition-colors duration-300 cursor-pointer whitespace-nowrap"
                  aria-label="Inscrever"
                >
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </button>
              </div>
              {status === "success" && (
                <p className="text-primary-300 text-xs font-light">
                  Inscrição realizada com sucesso!
                </p>
              )}
              {status === "error" && (
                <p className="text-red-400 text-xs font-light">
                  Ops! Algo deu errado. Tente novamente.
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-primary-500/15 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-background-100/45 text-xs font-light">
            &copy; {new Date().getFullYear()} Jennifer Siqueira Studio de Beleza. Todos os direitos reservados.
          </p>
          <p className="text-background-100/35 text-[10px] font-light tracking-wide">
            Design com excelência para beleza com propósito
          </p>
        </div>
      </div>

      <div className="overflow-hidden select-none pointer-events-none pb-4">
        <p className="font-heading text-[10vw] md:text-[8vw] font-bold text-primary-500/10 text-center leading-none whitespace-nowrap">
          Jennifer Siqueira
        </p>
      </div>
    </footer>
  );
}